import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Share2, Plus, Calendar, Eye, Edit, Trash2,
  Facebook, Instagram, Linkedin, Twitter, MessageCircle,
  Image as ImageIcon, Video, FileText, CheckCircle, Clock, AlertCircle,
  ChevronDown, ChevronRight
} from 'lucide-react';
import axiosInstance from '../../config/axios';
import { useContentFilters } from '../../hooks/useContentFilters';
import ContentFilterBar from './ContentFilterBar';
import ContentGroupView from './ContentGroupView';
import SocialAssetCreation from '../assets/by-type/SocialAssetCreation';

interface SocialPost {
  id: number;
  type: string;
  title: string;
  description?: string;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'pending_review' | 'approved' | 'published' | 'rejected';
  metadata?: string; // JSON string containing platform, content_type, caption, hashtags, etc.
  scheduled_for?: string;
  created_by?: string;
  client_id?: number;
  plan_id?: number;
  created_at: string;
  updated_at?: string;
  // From API joins
  client_name?: string;
  client_company?: string;
  project_name?: string;
  project_status?: string;
  // Parsed from metadata
  platform?: string;
  content_type?: string;
  project_id?: number;
  phase_name?: string;
  phase_id?: number;
  assigned_to?: string;
  priority?: string;
  tags?: string[];
  preview_image?: string;
  caption?: string;
  hashtags?: string[];
}

export default function SocialContentManagement() {
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Mock data for filters (replace with actual API calls)
  const mockClients = [
    { id: 1, name: 'Acme Corporation' },
    { id: 2, name: 'TechStart Inc' },
    { id: 3, name: 'Global Services LLC' },
  ];

  const mockProjects = [
    { id: 1, name: 'Spring Campaign 2024' },
    { id: 2, name: 'Brand Refresh' },
    { id: 3, name: 'Product Launch' },
  ];

  const mockPhases = [
    { id: 1, name: 'Planning & Strategy' },
    { id: 2, name: 'Content Creation' },
    { id: 3, name: 'Publishing' },
  ];

  const {
    filters,
    grouping,
    filteredItems,
    groupedItems,
    activeFilterCount,
    updateFilter,
    clearFilters,
    removeFilter,
    setGrouping,
    isGrouped,
  } = useContentFilters(posts, 'social');

  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  useEffect(() => {
    loadSocialContent();
  }, []);

  const loadSocialContent = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/content?type=social');
      if (response.data.items) {
        // Parse metadata for each post
        const parsedPosts = response.data.items.map((post: any) => {
          let metadata = {};
          try {
            metadata = post.metadata ? JSON.parse(post.metadata) : {};
          } catch (e) {
            console.warn('Failed to parse metadata for post:', post.id);
          }
          return {
            ...post,
            platform: metadata.platform || 'facebook',
            content_type: metadata.content_type || 'post',
            caption: metadata.caption || post.description,
            hashtags: metadata.hashtags || [],
            preview_image: metadata.preview_image || null,
            ...metadata
          };
        });
        setPosts(parsedPosts);
      }
    } catch (error) {
      console.error('Failed to load social content:', error);
      setPosts([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const getPlatformIcon = (platform?: string) => {
    if (!platform) return Share2;

    switch (platform.toLowerCase()) {
      case 'facebook': return Facebook;
      case 'instagram': return Instagram;
      case 'linkedin': return Linkedin;
      case 'twitter': return Twitter;
      default: return Share2;
    }
  };

  const getContentTypeBadge = (type?: string) => {
    if (!type) return 'bg-gray-100 text-gray-700';

    const colors: Record<string, string> = {
      post: 'bg-blue-100 text-blue-700',
      story: 'bg-purple-100 text-purple-700',
      reel: 'bg-pink-100 text-pink-700',
      video: 'bg-red-100 text-red-700',
      carousel: 'bg-green-100 text-green-700',
    };
    return colors[type.toLowerCase()] || 'bg-gray-100 text-gray-700';
  };

  const getPlatformColor = (platform: string) => {
    switch (platform?.toLowerCase()) {
      case 'facebook': return 'bg-blue-500';
      case 'instagram': return 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500';
      case 'linkedin': return 'bg-blue-700';
      case 'twitter': return 'bg-sky-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'published':
        return <span className="flex items-center px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
          <CheckCircle className="h-3 w-3 mr-1" /> Published
        </span>;
      case 'scheduled':
        return <span className="flex items-center px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
          <Clock className="h-3 w-3 mr-1" /> Scheduled
        </span>;
      case 'draft':
        return <span className="flex items-center px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
          <FileText className="h-3 w-3 mr-1" /> Draft
        </span>;
      case 'pending_review':
        return <span className="flex items-center px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
          <AlertCircle className="h-3 w-3 mr-1" /> Pending Review
        </span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">{status}</span>;
    }
  };

  const getContentTypeIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'image': return ImageIcon;
      case 'video': return Video;
      case 'carousel': return ImageIcon;
      case 'story': return MessageCircle;
      default: return FileText;
    }
  };

  // Render a single social post card
  const renderPostCard = (post: SocialPost) => {
    const PlatformIcon = getPlatformIcon(post.platform);
    const ContentIcon = getContentTypeIcon(post.content_type);

    return (
      <div className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow border border-gray-200 overflow-hidden">
        {/* Preview Image */}
        <div className="h-48 bg-gray-100 relative">
          {post.preview_image ? (
            <img
              src={post.preview_image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <ContentIcon className="h-12 w-12 text-gray-300" />
            </div>
          )}
          <div className={`absolute top-3 left-3 h-8 w-8 ${getPlatformColor(post.platform)} rounded-full flex items-center justify-center shadow-lg`}>
            <PlatformIcon className="h-4 w-4 text-white" />
          </div>
        </div>

        <div className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h3 className="text-base font-semibold text-gray-900 line-clamp-1">{post.title}</h3>
              {post.client_name && (
                <p className="text-xs text-gray-500">{post.client_name}</p>
              )}
            </div>
            {getStatusBadge(post.status)}
          </div>

          {/* Caption */}
          {post.caption && (
            <p className="text-sm text-gray-600 line-clamp-2 mb-3">{post.caption}</p>
          )}

          {/* Hashtags */}
          {post.hashtags && post.hashtags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {post.hashtags.slice(0, 3).map((tag, idx) => (
                <span key={idx} className="text-xs text-blue-600">#{tag}</span>
              ))}
              {post.hashtags.length > 3 && (
                <span className="text-xs text-gray-500">+{post.hashtags.length - 3} more</span>
              )}
            </div>
          )}

          {/* Schedule Info */}
          {post.scheduled_for && (
            <div className="flex items-center text-xs text-gray-500 mb-3">
              <Calendar className="h-3 w-3 mr-1" />
              {new Date(post.scheduled_for).toLocaleDateString()}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center space-x-2 pt-3 border-t border-gray-100">
            <button className="flex-1 flex items-center justify-center px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition text-sm font-medium">
              <Eye className="h-4 w-4 mr-1.5" />
              Preview
            </button>
            <button className="flex items-center justify-center px-3 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition">
              <Edit className="h-4 w-4" />
            </button>
            <button className="flex items-center justify-center px-3 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Social Media Content</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage social media posts across all platforms
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Plus className="h-5 w-5 mr-2" />
          Create Post
        </button>
      </div>

      {/* Filters */}
      <ContentFilterBar
        contentType="social"
        filters={filters}
        grouping={grouping}
        activeFilterCount={activeFilterCount}
        onFilterChange={updateFilter}
        onClearFilters={clearFilters}
        onRemoveFilter={removeFilter}
        onGroupingChange={(updates) => setGrouping({ ...grouping, ...updates })}
        clients={mockClients}
        projects={mockProjects}
        phases={mockPhases}
      />

      {/* Content Display - Table View */}
      {!isGrouped && filteredItems.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Post</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client / Project</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scheduled</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredItems.map((post) => {
                  const PlatformIcon = getPlatformIcon(post.platform);
                  return (
                    <tr key={post.id} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 p-2 bg-blue-50 rounded-lg">
                            <PlatformIcon className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{post.title}</div>
                            <div className="text-xs text-gray-500 truncate max-w-md">{post.caption || post.description}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{post.client_name || `Client #${post.client_id}`}</div>
                        <div className="text-xs text-gray-500">{post.project_name || 'No project'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 capitalize">{post.platform || 'Facebook'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getContentTypeBadge(post.content_type)}`}>
                          {post.content_type || 'Post'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(post.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {post.scheduled_for ? new Date(post.scheduled_for).toLocaleDateString() : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded">
                            <Eye className="h-4 w-4" />
                          </button>
                          <button className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded">
                            <Edit className="h-4 w-4" />
                          </button>
                          <button className="text-gray-600 hover:text-red-600 p-1 hover:bg-red-50 rounded">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Grouped View */}
      {isGrouped && (
        <div className="space-y-6">
          {Object.entries(groupedItems).map(([groupName, groupPosts]) => {
            const postsArray = Array.isArray(groupPosts) ? groupPosts : [];
            const isExpanded = expandedGroups.has(groupName);

            return (
            <div key={groupName} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleGroup(groupName)}
                className="w-full bg-gray-50 px-6 py-3 border-b border-gray-200 flex items-center justify-between hover:bg-gray-100 transition"
              >
                <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  {groupName} ({postsArray.length})
                </h3>
              </button>
              {isExpanded && (
              <div>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Post</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client / Project</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scheduled</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {postsArray.map((post: SocialPost) => {
                      const PlatformIcon = getPlatformIcon(post.platform);
                      const ContentIcon = getContentTypeIcon(post.content_type);
                      return (
                        <tr key={post.id} className="hover:bg-gray-50 transition">
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 p-2 bg-blue-50 rounded-lg">
                                <ContentIcon className="h-5 w-5 text-blue-600" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{post.title}</div>
                                <div className="text-xs text-gray-500">{post.caption?.slice(0, 50)}...</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">{post.client_name || `Client #${post.client_id || 'N/A'}`}</div>
                            <div className="text-xs text-gray-500">{post.project_name || 'No project'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <PlatformIcon className="h-4 w-4" />
                              <span className="text-sm text-gray-900 capitalize">{post.platform}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getContentTypeBadge(post.content_type)}`}>
                              {post.content_type}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(post.status)}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {post.scheduled_for ? new Date(post.scheduled_for).toLocaleDateString() : '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded">
                                <Eye className="h-4 w-4" />
                              </button>
                              <button className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded">
                                <Edit className="h-4 w-4" />
                              </button>
                              <button className="text-gray-600 hover:text-red-600 p-1 hover:bg-red-50 rounded">
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!isGrouped && filteredItems.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Share2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No social posts found</h3>
          <p className="text-gray-500 mb-6">Try adjusting your search or filters</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Plus className="h-5 w-5 mr-2" />
            Create Post
          </button>
        </div>
      )}

      {/* Create Post Modal - Full Screen */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-white z-50 overflow-auto">
          <div className="min-h-screen">
            {/* Header with Close Button */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-semibold text-gray-900">Create Social Media Post</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  loadSocialContent(); // Refresh data after creation
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Close
              </button>
            </div>

            {/* Render Social Asset Creation Component */}
            <div className="p-6">
              <SocialAssetCreation />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
